import { action, observable, makeObservable } from 'mobx';
import {
    http,
    logger,
    LogEvents,
    Timeouts,
    i18n,
    throttle,
    Guid,
    LogEvent,
    getObjectForLog,
} from 'helpers';
import { Receipts } from './Receipts';
import { BusMessage } from '../../platform/KioskSdk.Bus';
import {
    TransactionCompleteRequest,
    TransactionRequest,
    TransactionUpdateRequest,
} from 'interfaces';
import { BillpayResponse } from './BillpayResponse';
import StoreModel from 'models/Index';
import { TimeoutError } from 'helpers/TimeoutError';
import {
    CashReceivingResult,
    CashReceivingRequest,
} from '@b2bsoft/kiosk-common-sdk';
import { fillSdkStack, startProcessingCash } from 'sdk/InternalSdk';

export class Billpay {
    @observable public amountInserted: number = 0;
    @observable public isProcessingCash: boolean = false;
    @observable public processCreateFailed: boolean = false;
    @observable public processUpdateFailed: boolean = false;

    customer?: BillpayResponse | null;
    receipts: Receipts;

    public processId?: string;
    public transactionId: number | null = null;
    public confirmationError?: string;
    public resetCamundaTimerThrottled: Function;
    public userActivityListenerId?: number;
    public lastBanknoteId: string | null = null;
    public isProcessingComplete: boolean = false;
    private root: StoreModel;
    private hasSendedCashToCamunda: boolean = false;
    private isCompleteStarted: boolean = false;

    constructor(root: StoreModel) {
        this.root = root;
        this.receipts = new Receipts(root, this);
        this.resetCamundaTimerThrottled = throttle(
            this.resetCamundaTimer.bind(this),
            Timeouts.ResetTimerThrottle
        );
        makeObservable(this);
    }

    @action
    setCustomerData = async (data: BillpayResponse) => {
        this.customer = new BillpayResponse(data);
    };

    createTransaction = async (): Promise<string> => {
        if (!this.customer) {
            console.error('Billpay customer was not found...');
            return Promise.reject();
        }

        const request = {
            accountNumber: this.customer.accountNumber,
            messageBroker: this.root.configuration.messageBroker,
            appId: this.root.paymentAttributes.appId,
            sku: this.root.paymentAttributes.sku,
            description: this.root.paymentAttributes.description,
            serviceCode: this.root.paymentAttributes.serviceCode,
            paymentApiUrl: this.root.paymentAttributes.paymentApiUrl,
        } as TransactionRequest;

        this.root.loader.show();

        const waitProcessStarted =
            this.root.kioskSdkEvents.transactionProcessStarted({
                timeout: Timeouts.WaitProcessStartedTime,
            });
        try {
            let response = await http.post<string>('/api/payment', request);
            this.processId = response.data!;

            await waitProcessStarted;

            return response.data;
        } catch (error) {
            let eventType: LogEvent | null = null;
            if (error instanceof TimeoutError) {
                eventType = LogEvents.MessageBrokerTimeoutMessageNotReceived;
            } else {
                eventType = LogEvents.TransactionCreateFailed;
            }
            logger.logError('Failed to create transaction.', eventType, {
                error,
                billpay: getObjectForLog(this),
                transactionRequest: request,
                processInstanceId: this.processId,

            });

            this.setProcessCreateFailed(true);
            throw error;
        } finally {
            this.root.loader.hide();
        }
    };

    @action
    onCashEscrowed = async (event: Event): Promise<any> => {
        this.root.hideHomeButton();
        this.root.loader.show();
        this.lastBanknoteId = Guid.newGuid();

        const customEvent = event as CustomEvent;
        const data = customEvent.detail as CashReceivingResult;
        logger.localLog(
            'Received message from CashReceiver.',
            LogEvents.CashReceiverProccessed,
            data.amount,
            this.lastBanknoteId
        );

        try {
            //fill stack on sdk to be able call stack command
            fillSdkStack(this.root, {
                appId: this.root.paymentAttributes.appId,
                processId: this.processId,
                amount: data.amount,
            });

            const stackResult =
                await this.root.shellClient.cashAcceptor.processStackCash();

            if (stackResult && stackResult.eventRaised) {
                this.increaseAmountInserted(data.amount);
                this.setIsProcessingCash(true);
                logger.localLog(
                    'Stack banknote, success ',
                    LogEvents.StackCash,
                    data.amount,
                    this.lastBanknoteId
                );

                logger.logInformation(
                    'Stack banknote, success ',
                    LogEvents.StackCash,
                    {
                        billpay: getObjectForLog(this),
                        newAmount: data.amount,
                        processInstanceId: this.processId,
                    }
                );

                const request = {
                    id: this.lastBanknoteId,
                    amount: data.amount,
                } as TransactionUpdateRequest;

                http.put(`/api/payment/${this.processId}/add`, request).then(
                    () => {
                        this.hasSendedCashToCamunda = true;
                        logger.localLog(
                            'Send Amount to Camunda',
                            LogEvents.SendAmount,
                            data.amount,
                            this.lastBanknoteId
                        );
                    }
                );

                this.lastBanknoteId = null;

                const startData = {
                    appID: this.root.paymentAttributes.appId,
                    processId: String(this.processId),
                } as CashReceivingRequest;

                await startProcessingCash(this.root, startData, {
                    timeout: Timeouts.SdkCashReceiving,
                });
            } else {
                throw Error(`Failed to recceive stackResult for banknote ${data.amount} and camundaProcessId ${this.processId} this banknote will not be processed`);
            }
        } catch (error) {
            let eventType: LogEvent | null = null;
            if (error instanceof TimeoutError) {
                eventType = LogEvents.MessageBrokerTimeoutMessageNotReceived;
            } else {
                eventType = LogEvents.TransactionUpdateFailed;
            }

            logger.logError('Failed to update transaction.', eventType, {
                error,
                billpay: getObjectForLog(this),
                newAmount: data.amount,
                processInstanceId: this.processId,
            });

            throw error;
        } finally {
            this.root.loader.hide();
        }
    };

    completeTransaction = () => {
        const loaderText = this.processUpdateFailed
            ? i18n.t(
                'Something went wrong, please wait when transaction will be completed...'
            )
            : i18n.t('TransactionCompletionMsg');

        this.root.loader.show(loaderText);

        const request = {
            amount: this.amountInserted,
        } as TransactionCompleteRequest;
        const waitTransactionCompleted =
            this.root.kioskSdkEvents.transactionCompleted({
                timeout: Timeouts.WaitPaymentCompletedTime,
                relevantProcessId: this.processId,
            });

        this.isProcessingComplete = true;
        this.isCompleteStarted = true;

        return http
            .post<any>(`/api/payment/${this.processId}/complete`, request)
            .then(() => waitTransactionCompleted)
            .then((data) => {
                this.transactionId = data.transationId;

                return data;
            })
            .catch((error) => {
                let eventType: LogEvent | null = null;
                if (error instanceof TimeoutError) {
                    eventType =
                        LogEvents.MessageBrokerTimeoutMessageNotReceived;
                } else {
                    eventType = LogEvents.TransactionCompleteFailed;
                }

                logger.logError('Failed to complete transaction.', eventType, {
                    error,
                    billpay: getObjectForLog(this),
                    processInstanceId: this.processId,
                });

                this.confirmationError = 'Unexpected error happened.';

                if (error instanceof BusMessage) {
                    this.transactionId = error.transationId;
                    this.confirmationError =
                        error.errorMessage || this.confirmationError;
                }

                throw error;
            })
            .finally(() => {
                this.setIsProcessingCash(false);
                this.root.loader.hide();
                this.isProcessingComplete = false;
            });
    };

    onUserActivity(): Promise<any> {
        return this.resetCamundaTimerThrottled();
    }

    @action
    increaseAmountInserted = (amount: number) => {
        this.amountInserted += amount;
    };
    @action
    setAmountInserted = (amount: number) => {
        this.amountInserted = amount;
    };

    private resetCamundaTimer = () => {
        if (
            !this.processId ||
            this.transactionId ||
            this.isCompleteStarted ||
            !this.root.peripheryStatus.kioskOnline
        ) {
            return Promise.resolve();
        }

        return http.put<any>(
            `/api/payment/reset-timer/${this.processId}`,
            { hasInsertedCash: this.hasSendedCashToCamunda },
            {
                disableLoader: true,
            } as any
        );
    };

    @action
    public setIsProcessingCash = (value: boolean) => {
        this.isProcessingCash = value;
    };

    reset() {
        this.customer = null;
        this.setAmountInserted(0);
        this.processId = undefined;
        this.transactionId = null;
        this.isCompleteStarted = false;
        this.hasSendedCashToCamunda = false;
        this.setProcessCreateFailed(false);
        this.setProcessUpdateFailed(false);
        this.confirmationError = undefined;
        this.receipts.reset();
        this.root.wizard.reset();
    }

    @action
    setProcessUpdateFailed = (value: boolean) => {
        this.processUpdateFailed = value;
    };

    @action
    setProcessCreateFailed = (value: boolean) => {
        this.processCreateFailed = value;
    };

    resetTraceId() {
        // Reset traceContext traceId
        this.root.traceContext.start();
        this.root.shellClient.api.setTraceId(this.root.traceContext.traceId);

        console.log(`New TraceId: ${this.root.traceContext.traceId}`);
    }

    /**
     *  if transaction was success both in our system and AMSS
     * @memberof Billpay
     */
    get isSuccess() {
        return this.isTransactionCreated && !this.confirmationError;
    }

    /**
     * if transaciton was success in our system but failed in AMSS
     * @memberof Billpay
     */
    get isFailedInAmss() {
        return !!this.transactionId && !!this.confirmationError;
    }

    get processFailed(): boolean {
        return this.processCreateFailed || this.processUpdateFailed;
    }

    get isTransactionCreated() {
        return this.transactionId != null;
    }

    get hasInsertedCash() {
        return this.amountInserted > 0;
    }

    get hasNextBalanceDue() {
        return this.nextBalanceDue > 0;
    }

    get accountNumberLast4() {
        if (!this.customer) {
            return '';
        }

        return this.customer.accountNumber.substr(
            this.customer.accountNumber.length - 4
        );
    }

    get pastBalanceDueLeft() {
        if (!this.customer) {
            return 0;
        }

        const diff = this.customer.pastDueAmount - this.amountInserted;

        return Math.max(0, diff);
    }

    get currentBalanceDueLeft() {
        if (!this.customer) {
            return 0;
        }

        const diff =
            this.customer.currentBalance +
            this.customer.pastDueAmount -
            this.amountInserted;

        return this.pastBalanceDueLeft > 0
            ? this.customer.currentBalance
            : Math.max(0, diff);
    }

    get totalAmountDueLeft() {
        return this.pastBalanceDueLeft + this.currentBalanceDueLeft;
    }

    get nextBalanceDue() {
        if (this.customer && this.amountInserted && !this.totalAmountDueLeft) {
            return (
                this.amountInserted -
                Math.max(0, this.customer.currentBalance) -
                Math.max(0, this.customer.pastDueAmount)
            );
        }

        return 0;
    }
}
